/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useDashboardBusinessContextKpiWidgetsQuery } from "~/generated/graphql";
import { useCurrency } from "~/hooks";
import { formatMoney } from "~/tools";

type Data = ReturnType<
  typeof useDashboardBusinessContextKpiWidgetsQuery
>["data"];

export function useKpiWidgetData(businessContextId?: string) {
  const { data, loading, error } = useDashboardBusinessContextKpiWidgetsQuery({
    variables: { bcId: businessContextId ?? "" },
    skip: !businessContextId,
  });
  const { currency } = useCurrency();

  const { costAllocationCoverage, overallWaste, nonProdHours } = React.useMemo(
    () => mapData(data, currency),
    [currency, data]
  );

  return {
    loading,
    error,
    costAllocationCoverage,
    overallWaste,
    nonProdHours,
  };
}

function mapData(data: Data, currency: string) {
  const { costAllocationCoverage, overallWaste, nonProdHours } =
    data?.businessContext?.kpis ?? {};

  const allocatedCost = data?.businessContext?.costAllocation?.allocated;

  return {
    costAllocationCoverage: {
      value: costAllocationCoverage?.details?.metrics?.currentValue,
      target: costAllocationCoverage?.details?.targets?.currentTarget?.goal,
      additionalValue: formatMoney(allocatedCost, currency),
    },
    overallWaste: {
      value: overallWaste?.details?.metrics?.currentValue,
      target: overallWaste?.details?.targets?.currentTarget?.goal,
      additionalValue: formatMoney(overallWaste?.totalWaste, currency),
    },
    nonProdHours: {
      value: nonProdHours?.details?.metrics?.currentValue,
      target: nonProdHours?.details?.targets?.currentTarget?.goal,
      additionalValue: nonProdHours?.totalHours
        ? `${nonProdHours?.totalHours} h`
        : "",
    },
  };
}
